import React from "react";
import { Link } from "react-router-dom";
import { useBoilerRegister } from "../../hooks/useBoilerRegister";
import { PageHeader, SectionCard, Pill } from "../../components/Ui";
import { Button } from "../../components/ui/button";
import { INDIA_LOCALE, INDIA_TIME_ZONE } from "../../lib/indiaTime";
import { ArrowLeft, RefreshCw } from "lucide-react";

function formatWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(INDIA_LOCALE, {
    timeZone: INDIA_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(seconds) {
  if (seconds == null) return "—";
  const total = Math.max(0, Math.floor(Number(seconds)));
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours) return `${hours}h ${mins}m ${secs}s`;
  if (mins) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

const EVENT_TONE = {
  boiler_started: "success",
  operator_shift_start: "info",
  hourly_reading: "default",
  water_test: "sage",
  blowdown: "info",
  blowdown_started: "warning",
  operator_shift_end: "default",
  boiler_stopped: "default",
  boiler_breakdown: "danger",
  breakdown_reported: "danger",
};

export default function BoilerRegisterPage() {
  const { entries, total, loading, reload } = useBoilerRegister({ limit: 200 });

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="boiler-register-page">
      <PageHeader
        eyebrow="Production · Boiler"
        title="Boiler log register"
        description="Complete activity history — what happened, when, who operated, and duration."
        actions={(
          <div className="flex flex-col sm:flex-row gap-2">
            <Button asChild variant="outline" className="min-h-11">
              <Link to="/production/boiler">
                <span className="inline-flex items-center">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Workspace
                </span>
              </Link>
            </Button>
            <Button type="button" variant="outline" className="min-h-11" onClick={reload} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        )}
      />

      <SectionCard title={`Activity log (${total} events)`} testId="boiler-register-table">
        {loading && !entries.length ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading register…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No boiler activity recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-3 whitespace-nowrap">When</th>
                  <th className="py-2 pr-3">Activity</th>
                  <th className="py-2 pr-3">Details</th>
                  <th className="py-2 pr-3">Operator</th>
                  <th className="py-2 whitespace-nowrap">Duration</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((row) => (
                  <tr key={row.id} className="border-b border-border/50 align-top">
                    <td className="py-2.5 pr-3 tabular-nums whitespace-nowrap text-xs">{formatWhen(row.occurred_at)}</td>
                    <td className="py-2.5 pr-3">
                      <Pill tone={EVENT_TONE[row.event_type] || "default"}>{row.activity}</Pill>
                    </td>
                    <td className="py-2.5 pr-3 text-foreground/85 max-w-md">{row.details || "—"}</td>
                    <td className="py-2.5 pr-3 whitespace-nowrap">{row.operator_name || "—"}</td>
                    <td className="py-2.5 tabular-nums whitespace-nowrap">{formatDuration(row.duration_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
